import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/Auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/useToast';
import { AUTH_ROUTES } from '@/utils/auth';
import { MailCheck } from 'lucide-react';

/**
 * Changing the address the account signs in with — and, because every
 * notification trigger reads auth.users.email directly, the address every
 * email from Dialup goes to.
 *
 * Collapsed into the account page next to Change password, which it mirrors:
 * the current password is required, and is checked by signing in with it
 * rather than trusting the open session.
 */
const ChangeEmail: React.FC = () => {
  const { t } = useTranslation();
  const { user, signIn, updateEmail, hasPasswordIdentity } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // A Google-only account has no password to check, and its address comes
  // from the provider. Setting one through the reset flow turns it into an
  // account that can do this; until then there is nothing to confirm against.
  if (!hasPasswordIdentity) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {t(
            'profile.change_email.google_only.translation',
            'This account signs in with Google, so it has no password to confirm the change with. Set one first and you can change the address here afterwards.'
          )}
        </p>
        <Button variant="outline" asChild className="w-full sm:w-auto">
          <Link to={AUTH_ROUTES.forgotPassword}>
            {t(
              'profile.change_email.set_password.translation',
              'Set a password'
            )}
          </Link>
        </Button>
      </div>
    );
  }

  if (sentTo) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg bg-card p-3">
        <MailCheck
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <p className="text-sm text-muted-foreground">
          {t('profile.change_email.sent.translation', {
            defaultValue:
              'Check {{email}} for a confirmation link. Your address stays as it is until you follow it, and we also tell your current address that the change was requested.',
            email: sentTo,
          })}
        </p>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const address = newEmail.trim();
    if (address.toLowerCase() === (user?.email ?? '').toLowerCase()) {
      toast({
        variant: 'destructive',
        title: t('profile.change_email.error.title.translation', 'Not changed'),
        description: t(
          'profile.change_email.error.same.translation',
          'That is already the address on this account.'
        ),
      });
      return;
    }

    setIsSaving(true);
    try {
      // Re-authenticate rather than trust the open session: the same guard
      // Change password uses, and the reason the current password is asked for.
      const { error: signInError } = await signIn({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        toast({
          variant: 'destructive',
          title: t(
            'profile.change_email.error.title.translation',
            'Not changed'
          ),
          description: t(
            'change_password.toast.error.incorrect_current.translation'
          ),
        });
        return;
      }

      const error = await updateEmail(address);
      if (error) {
        toast({
          variant: 'destructive',
          title: t(
            'profile.change_email.error.title.translation',
            'Not changed'
          ),
          description: error.message,
        });
        return;
      }

      setSentTo(address);
      setCurrentPassword('');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'profile.change_email.description.translation',
          'This is the address you sign in with, and where every email from Dialup is sent.'
        )}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-email">
          {t('profile.change_email.new.translation', 'New email address')}
        </Label>
        <Input
          id="new-email"
          type="email"
          required
          autoComplete="email"
          value={newEmail}
          onChange={event => setNewEmail(event.target.value)}
          disabled={isSaving}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="current-password-email">
          {t(
            'profile.change_email.current_password.translation',
            'Current password'
          )}
        </Label>
        <Input
          id="current-password-email"
          type="password"
          required
          autoComplete="current-password"
          value={currentPassword}
          onChange={event => setCurrentPassword(event.target.value)}
          disabled={isSaving}
        />
      </div>
      <Button
        type="submit"
        variant="brand"
        className="w-full sm:w-auto"
        disabled={isSaving || !newEmail.trim() || !currentPassword}
      >
        {isSaving
          ? t('common.saving.translation', 'Saving...')
          : t(
              'profile.change_email.submit.translation',
              'Send confirmation link'
            )}
      </Button>
    </form>
  );
};

export default ChangeEmail;
